import { motion } from 'framer-motion';
import { PieChart, Shield, Landmark, Briefcase, Building2, HeartPulse } from 'lucide-react';

/* ═══════════════════════════════════════════
   EDITABLE: Services data
═══════════════════════════════════════════ */
const services = [
  {
    icon: PieChart,
    title: '360° Financial Assessment',
    shortDesc: 'Gain a complete analysis of your financial health to achieve your goals with confidence.',
    fullDesc: 'Optimize your finances with our expert 360° Financial Assessment — start today!',
  },
  {
    icon: Shield,
    title: 'Insurance Assessment',
    shortDesc: 'Evaluate your coverage needs to ensure complete financial protection for you and your family.',
    fullDesc: 'Get a personalized insurance assessment and secure your future today!',
  },
  {
    icon: Landmark,
    title: 'Retirement Planning',
    shortDesc: 'Plan ahead with a thorough retirement assessment to achieve financial independence.',
    fullDesc: 'Ensure a stress-free retirement with our expert assessment — start planning today!',
  },
  {
    icon: Briefcase,
    title: 'Portfolio Management',
    shortDesc: 'Optimize your investments with a professionally managed and diversified portfolio.',
    fullDesc: 'Let our experts manage your portfolio for maximum growth and risk control — start today!',
  },
  {
    icon: Building2,
    title: 'Tax Planning & GST',
    shortDesc: 'Professional Income Tax and GST compliance services to maximize your savings.',
    fullDesc: 'Reduce tax burdens and organize your finances with our thoughtful planning steps.',
  },
  {
    icon: HeartPulse,
    title: 'Health & Life Insurance',
    shortDesc: 'Protect your future with comprehensive insurance solutions tailored to your needs.',
    fullDesc: 'Secure your life, health, and assets with our expert insurance plans — get covered today!',
  },
];

export const ServicesSection = () => {
  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#D4AF37] mb-4">
            What We Do
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#0A192F]">
            Services We Offer
          </h2>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              data-testid={`service-card-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[260px]"
            >
              {/* Front Face */}
              <div className="absolute inset-0 p-8 flex flex-col items-center text-center transition-transform duration-500 group-hover:-translate-y-full">
                <div className="w-16 h-16 bg-[#F8F9FA] rounded-full flex items-center justify-center mb-5">
                  <service.icon size={28} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#0A192F] mb-3">
                  {service.title}
                </h3>
                <p className="text-sm text-[#49566B] leading-relaxed font-['Outfit']">
                  {service.shortDesc}
                </p>
              </div>

              {/* Back Face (on hover) */}
              <div className="absolute inset-0 p-8 flex flex-col items-center justify-center text-center translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-[#0A192F]">
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#D4AF37] mb-3">
                  {service.title}
                </h3>
                <p className="text-sm text-white/80 leading-relaxed font-['Outfit']">
                  {service.fullDesc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
